import * as React from "react";
import {
    IAppointmentHistory,
    IClient,
    IACLEmail,
    ClientStatus,
} from "intake_shared/src/types";
import { uniqBy } from "lodash";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { AclContext } from "../../../../../contexts/AclContext";
import { updateArchivedClient } from "../../../../../store/archive";
import { updateClientData } from "../../../../../store/client";
import styles from "./AppointmentHistory.module.scss";
import { fetchUserAcls } from "../../../../../store/acl";
import { cn } from "../../../../helpers";
import EmptyListState from "../../../bits/EmptyListState/EmptyListState";
import AppointmentCard from "./AppointmentCard/AppointmentCard";
import { fetchCliniciansByIds } from "../../../../../store/clinician";

interface AppointmentHistoryProps {
    initialHistories?: IAppointmentHistory[];
    client: IClient;
    showHeader?: boolean;
    viewFrom?: "side-bar" | "full-page";
}

export default function AppointmentHistory({
    initialHistories = [],
    client,
    showHeader = true,
    viewFrom = "side-bar",
}: AppointmentHistoryProps) {
    const olc = React.useContext(CacheContext);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [histories, setHistories] =
        React.useState<IAppointmentHistory[]>(initialHistories);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const [editMode, setEditMode] = React.useState<{ [id: string]: boolean }>({});
    const [editedNotes, setEditedNotes] = React.useState<{ [id: string]: string }>(
        {}
    );
    const [loading, setLoading] = React.useState<{ [id: string]: boolean }>({});

    const [expandedCards, setExpandedCards] = React.useState<{
        [id: string]: boolean;
    }>({});

    const [userAcls, setUserAcls] = React.useState<{
        [email: string]: IACLEmail | undefined;
    }>({});
    const [clinicianCredentialById, setClinicianCredentialById] = React.useState<
        Record<string, string>
    >({});

    const allUserRoles = olc?.allUserRoles;
    React.useEffect(() => {
        const fetchAcls = async () => {
            // The signed-in user's ACL gates note editing, so always read it
            // fresh; a permission revoked mid-session must take effect.
            const cachedAcls = (allUserRoles || []).filter(
                (acl) => acl.email !== loggedInUserEmail
            );
            const aclData = await fetchUserAcls(histories, cachedAcls);
            setUserAcls(aclData);
        };
        fetchAcls();
    }, [histories, allUserRoles, loggedInUserEmail]);

    React.useEffect(() => {
        const fetchClinicianCredentials = async () => {
            const clinicianIds = Array.from(
                new Set(histories.map((h) => h.clinician_id).filter(Boolean))
            );
            if (clinicianIds.length === 0) {
                setClinicianCredentialById({});
                return;
            }

            const clinicians = await fetchCliniciansByIds([...clinicianIds]);
            const credentialsMap = clinicians.reduce<Record<string, string>>(
                (acc, clinician) => {
                    if (clinician.id && clinician.credential) {
                        acc[clinician.id] = clinician.credential;
                    }
                    return acc;
                },
                {}
            );
            setClinicianCredentialById(credentialsMap);
        };

        fetchClinicianCredentials();
    }, [histories]);

    const handleToggleExpand = (id: string) => {
        setExpandedCards((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleEditClick = (id: string, currentNotes: string) => {
        setEditMode((prev) => ({ ...prev, [id]: true }));
        setEditedNotes((prev) => ({ ...prev, [id]: currentNotes }));
    };

    const handleCancelEdit = (id: string) => {
        setEditMode((prev) => ({ ...prev, [id]: false }));
        setEditedNotes((prev) => ({ ...prev, [id]: "" }));
    };

    const handleSaveClick = async (id: string) => {
        setLoading((prev) => ({ ...prev, [id]: true }));
        const newNotes = editedNotes[id];
        try {
            // Find the specific appointment history  to update
            const updatedHistories = histories.map((history) =>
                history.id === id
                    ? { ...history, cancellation_notes: newNotes }
                    : history
            );

            const updatedClient = {
                ...client,
                appointment_history: updatedHistories,
                last_updated_by: loggedInUserEmail as string,
            };

            if (client.status === ClientStatus.COMPLETED) {
                await updateArchivedClient(client.id, updatedClient);
            } else {
                await updateClientData({
                    client_id: client.id,
                    changes: { appointment_history: updatedHistories },
                    last_updated_by: loggedInUserEmail as string,
                });
            }

            setHistories(updatedHistories);
        } catch (error) {
            console.error(`Error updating notes for appointment ID ${id}:`, error);
        } finally {
            setLoading((prev) => ({ ...prev, [id]: false }));
        }

        setEditMode((prev) => ({ ...prev, [id]: false }));
    };

    const handleInputChange = (id: string, value: string) => {
        setEditedNotes((prev) => ({ ...prev, [id]: value }));
    };

    const uniqueHistories = uniqBy(histories, "id");

    return histories && histories.length > 0 ? (
        <div
            className={cn(
                styles.appointmentHistoryContainer,
                viewFrom === "full-page" && styles.appointmentHistoryContainerPage
            )}
        >
            {showHeader && (
                <div className={styles.appointmentHistoryHeader}>
                    <h6>Appointment History ({uniqueHistories.length})</h6>
                </div>
            )}
            <div className={styles.appointmentHistoryList}>
                {uniqueHistories.map((appointment, index) => (
                    <AppointmentCard
                        key={`appointment-${appointment.id}-${index}`}
                        appointment={appointment}
                        viewFrom={viewFrom}
                        index={index}
                        isExpanded={expandedCards[appointment.id] || false}
                        onToggleExpand={() => handleToggleExpand(appointment.id)}
                        editMode={editMode}
                        editedNotes={editedNotes}
                        onEditClick={handleEditClick}
                        onCancelEdit={handleCancelEdit}
                        onSaveClick={handleSaveClick}
                        onInputChange={handleInputChange}
                        loading={loading}
                        loggedInUserEmail={loggedInUserEmail || undefined}
                        userAcls={userAcls}
                        allDepartmentsAndServices={allDepartmentsAndServices}
                        clinicianCredentialById={clinicianCredentialById}
                    />
                ))}
            </div>
        </div>
    ) : (
        <EmptyListState
            emptyMessage="No appointment history"
            stateHelperText="No appointment history found"
        />
    );
}
